import { useMemo } from 'react';
import { EmployerUserContact, EntityId, FlexibleSlot, User } from '@restworld/utility-types';
import { ENDPOINTS } from '../../endpoints';
import { Service } from '../base';
import dynamicAxios from '../../utils/axios';
import {
  FetchForm,
  FlexibleSlotDiscountType,
  IndexFlexibleSlot,
  ForgotPassword,
  Jwt,
  Login,
  Me,
  NewLogin,
  PasswordlessLogin,
  IndexEmployerUserContactResponse,
  UserExistenceCheck,
  CancelSlotsParams,
  NextPaymentDetails,
  ConsumptionDetails,
} from './types';
import { Id } from '../../utils/common_types';
import useServices from '../../hooks/useServices';

type FlexibleSlotPurchaseParams = {
  companyId?: string;
  slots?: number;
  discount?: string;
  type?: 'percentage' | 'fixed';
  product?: 'trial' | 'standard';
};

class ServiceAccounts extends Service {
  me() {
    return this.axiosInstance.get<Me>(ENDPOINTS.accounts.me);
  }

  fetchUserType({ email }: { email?: string }) {
    return (
      this,
      this.axiosInstance.get<{ type: string }>(`${ENDPOINTS.accounts.root}/user_type`, {
        params: { email },
      })
    );
  }
  migrateUser({ email, toType }: { email?: string; toType?: string }) {
    return this.axiosInstance.post<string>(`${ENDPOINTS.accounts.root}/migrate_user`, {
      email,
      to_type: toType,
    });
  }

  checkUserExistence({ email, phone_number }: { email?: string; phone_number?: string }) {
    return this.axiosInstance.post<UserExistenceCheck>(ENDPOINTS.accounts.checkUserExistence, {
      email,
      phone_number,
    });
  }

  login({ email, password }: { email: string; password: string }) {
    return this.axiosInstance.post<Login>(
      ENDPOINTS.accounts.login,
      { email, password },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer null`,
        },
      }
    );
  }

  createSetupCheckoutSession({ employerId, companyId }: { employerId?: string; companyId?: string }) {
    return this.axiosInstance.get<string>(ENDPOINTS.accounts.setupCheckoutSession, {
      params: { employer_id: employerId, company_id: companyId },
    });
  }

  getIntercomContactId(email: string) {
    return this.axiosInstance.get<Id>(ENDPOINTS.accounts.getIntercomContactId, {
      params: { email },
    });
  }

  passwordForgot(email?: string) {
    return this.axiosInstance.get<ForgotPassword>(ENDPOINTS.accounts.forgotPassword, {
      params: {
        email: email,
      },
    });
  }

  fetchForm(id: string) {
    return this.axiosInstance.get<FetchForm>(`${ENDPOINTS.accounts.formId}/${id}`);
  }

  fetchFormSession(form_id: string) {
    return this.axiosInstance.get<Id>(ENDPOINTS.accounts.formSessionId, {
      params: {
        form_id,
      },
    });
  }

  resetPassword({ id, newPassword, jwt }: { id: EntityId; newPassword: string; jwt: string }) {
    return this.axiosInstance.post<Jwt>(
      ENDPOINTS.accounts.resetPassword,
      {
        id,
        password: newPassword,
      },
      { headers: { Authorization: `Bearer ${jwt}` } }
    );
  }

  // For new sign_up
  createAccount({ email, phone_number, redirectURL }: { email: string; phone_number?: string; redirectURL?: string }) {
    return this.axiosInstance.post(ENDPOINTS.accounts.signUp, {
      email,
      phone_number,
      redirect_url: redirectURL,
    });
  }

  signUp({ id, email, password }: { id: string; email: string; password: string }) {
    return this.axiosInstance.post<Jwt>(ENDPOINTS.accounts.signUp, { id, email, password });
  }

  updateUser(params: Partial<User>) {
    return this.axiosInstance.patch<string>(ENDPOINTS.accounts.root, params);
  }

  passwordlessLogin(email: string, redirectUrl: string) {
    return this.axiosInstance.post<PasswordlessLogin>(ENDPOINTS.accounts.passwordlessLogin, {
      email: email,
      redirect_url: redirectUrl,
    });
  }

  // Passwordless login by phone_number
  phoneNumberLogin(phoneNumber: string) {
    return this.axiosInstance.post<PasswordlessLogin>(ENDPOINTS.accounts.passwordlessLogin, {
      phone_number: phoneNumber,
    });
  }

  // when requested login by phone_number
  verifyOTPCode({ otpCode, phoneNumber }: { otpCode: string; phoneNumber: string }) {
    return this.axiosInstance.post(ENDPOINTS.accounts.login, {
      otp_code: otpCode,
      phone_number: phoneNumber,
    });
  }

  // when requested login by email
  /**
   * Spend a magic-link token and get the session back.
   *
   * `redirectUrl` is where the link said it was going, as the address bar had it. Since
   * September 2026 the backend records the destination on the credential's own row and answers
   * with it, so this argument only matters for a link minted before that — but sending it means
   * the frontend has **one** place to read the destination from (the response) instead of
   * carrying it between three components through the URL, which is the handover that lost it
   * on every cold load for twenty days. The backend refuses anything that is not a path inside
   * the app before echoing it, so this cannot be turned into an open redirect.
   */
  verifyMagicLink(token: string, redirectUrl?: string) {
    return this.axiosInstance.post<NewLogin>(ENDPOINTS.accounts.login, {
      token,
      ...(redirectUrl ? { redirect_url: redirectUrl } : {}),
    });
  }

  sendVerificationPhoneOtp(params?: {
    phoneNumber?: string;
    gmapsPlaceId?: string;
    existingUserId?: string;
    channel?: 'sms' | 'whatsapp';
  }) {
    const { phoneNumber, gmapsPlaceId, existingUserId, channel } = params || {};
    return this.axiosInstance.get<string>(ENDPOINTS.accounts.phoneOtpVerification, {
      params: {
        ...(existingUserId ? { existing_user_id: existingUserId } : { phone_number: phoneNumber }),
        ...(gmapsPlaceId ? { gmaps_place_id: gmapsPlaceId } : {}),
        ...(channel ? { channel } : {}),
      },
    });
  }

  // for verifying the phone_number on user_settings
  verifyPhoneOtp({ otpCode, gmapsPlaceId, channel }: { otpCode: string; gmapsPlaceId?: string; channel?: string }) {
    return this.axiosInstance.post<string>(ENDPOINTS.accounts.verifyPhoneOtp, {
      otp_code: otpCode,
      ...(gmapsPlaceId ? { gmaps_place_id: gmapsPlaceId } : {}),
      ...(channel ? { channel } : {}),
    });
  }

  updateAuthenticationDetails({ email, phoneNumber }: { email?: string; phoneNumber?: string }) {
    return this.axiosInstance.patch<string>(ENDPOINTS.accounts.updateAuthenticationEmail, {
      email,
      phone_number: phoneNumber,
    });
  }

  verifyEmail({ token }: { token: string }) {
    return this.axiosInstance.post<string>(ENDPOINTS.accounts.verifyEmail, { token });
  }
  // When user logs out
  logout() {
    return this.axiosInstance.get<string>(ENDPOINTS.accounts.logout);
  }

  // Delete account
  delete({ userId }: { userId: string }) {
    return this.axiosInstance.delete<string>(`${ENDPOINTS.accounts.delete}${userId ? `/${userId}` : ''}`);
  }

  // GET RESTAURANT EXPLORATION POPUP DETAILS
  restaurant_exploration_popup_details() {
    return this.axiosInstance.get<{
      restaurant_exploration_popup_show?: boolean;
      restaurant_exploration_popup_read_at?: string;
    }>(`${ENDPOINTS.accounts.restaurant_exploration_popup_details}`);
  }

  listFlexibleSlots({
    limit,
    offset,
    organizationId,
    status,
    dateTruncExpiresAt,
  }: {
    limit: number;
    offset: number;
    organizationId?: string;
    status?: string | string[];
    dateTruncExpiresAt?: boolean;
  }) {
    return this.axiosInstance.get<IndexFlexibleSlot>(ENDPOINTS.accounts.flexibleSlots, {
      params: { limit, offset, organization_id: organizationId, status, date_trunc_expires_at: dateTruncExpiresAt },
    });
  }

  cancelFlexibleSlot({ slotIds }: { slotIds?: number[] }) {
    return this.axiosInstance.post<string>(ENDPOINTS.accounts.cancelFlexibleSlot, { slot_ids: slotIds });
  }

  activateFlexibleSlot({ organizationId }: { organizationId: string }) {
    return this.axiosInstance.post<string>(ENDPOINTS.accounts.activateFlexibleSlot, {
      organization_id: organizationId,
    });
  }

  updateFlexibleSlot({ id, data }: { id?: number; data?: Partial<FlexibleSlot> }) {
    return this.axiosInstance.patch<string>(`${ENDPOINTS.accounts.flexibleSlots}/${id}`, data);
  }

  issueFreeSlot({ companyId }: { companyId?: string }) {
    return this.axiosInstance.post<string>(ENDPOINTS.accounts.freeSlot, {
      company_id: companyId,
    });
  }

  getFlexibleSlotPaymentSession({
    companyId,
    slots,
    type,
    discount,
    tag,
    autonomous,
    cancel_url,
    coupon,
    product,
  }: {
    companyId?: string;
    slots?: number;
    type?: FlexibleSlotDiscountType;
    discount?: string;
    tag?: string;
    autonomous?: string;
    cancel_url?: string;
    coupon?: string;
    product?: string;
  }) {
    return this.axiosInstance.post<string>(ENDPOINTS.accounts.flexibleSlotPaymentSession, {
      company_id: companyId,
      slots,
      type,
      discount,
      tag,
      autonomous,
      cancel_url,
      coupon,
      product,
    });
  }

  listEmployerUserContacts({
    limit,
    offset,
    organizationId,
    searchVector,
  }: {
    organizationId?: string;
    limit?: number;
    offset?: number;
    searchVector?: string;
  }) {
    return this.axiosInstance.get<IndexEmployerUserContactResponse>(ENDPOINTS.accounts.employerUserContact, {
      params: { limit, offset, organization_id: organizationId, search_vector: searchVector },
    });
  }

  createEmployerUserContact(data: any) {
    return this.axiosInstance.post<Id>(ENDPOINTS.accounts.employerUserContact, data);
  }

  fetchEmployerUserContact(user_id?: string) {
    return this.axiosInstance.get<EmployerUserContact>(`${ENDPOINTS.accounts.employerUserContact}/${user_id}`);
  }

  updateEmployerUserContact(params: any, userId?: string) {
    const headers =
      params instanceof FormData
        ? {
            'content-type': 'multipart/form-data',
          }
        : {
            'content-type': 'application/json',
          };
    return this.axiosInstance.patch<Id>(`${ENDPOINTS.accounts.employerUserContact}/${userId}`, params, {
      headers,
    });
  }

  deleteEmployerUserContact({ userId }: { userId?: string }) {
    return this.axiosInstance.delete<string>(`${ENDPOINTS.accounts.employerUserContact}/${userId}`);
  }

  getEmployerUserContactAccessLink(eucId?: string) {
    return this.axiosInstance.get(ENDPOINTS.accounts.employerUserContactAccessLink, {
      params: { user_id: eucId },
    });
  }

  getUserAccessLink(userId?: string) {
    return this.axiosInstance.get<string>(ENDPOINTS.accounts.userAccessLink, {
      params: { user_id: userId },
    });
  }

  deleteSecondaryContact({ id }: { id?: number }) {
    return this.axiosInstance.delete<string>(`${ENDPOINTS.accounts.secondaryContacts}/${id}`);
  }

  cancelSlots({
    stripeSubscriptionId,
    flexibleSlotIds,
    employerSubscriptionFeedback,
    createFeedback = true,
  }: CancelSlotsParams) {
    return this.axiosInstance.post<string>(ENDPOINTS.accounts.cancelSlots, {
      create_feedback: createFeedback,
      stripe_subscription_id: stripeSubscriptionId,
      slot_ids: flexibleSlotIds,
      ...employerSubscriptionFeedback,
    });
  }

  flexibleSlotInstallmentPayment({ companyId, slots, discount, type }: FlexibleSlotPurchaseParams) {
    return this.axiosInstance.post<string>(`${ENDPOINTS.accounts.flexibleSlots}/pay_in_installments`, {
      company_id: companyId,
      slots,
      type,
      discount,
    });
  }

  buyFlexibleSlots({ companyId, slots, discount, type, product }: FlexibleSlotPurchaseParams) {
    return this.axiosInstance.post<string>(`${ENDPOINTS.accounts.flexibleSlots}/buy`, {
      company_id: companyId,
      slots,
      type,
      discount,
      product,
    });
  }

  createAnnualSubscription({ companyId, slots }: { companyId?: string; slots?: number }) {
    return this.axiosInstance.post<string>(ENDPOINTS.accounts.annualSubscription, {
      company_id: companyId,
      slots: slots,
    });
  }

  getNextPaymentDetails() {
    return this.axiosInstance.get<NextPaymentDetails>(ENDPOINTS.accounts.nextPaymentDetails);
  }

  getConsumptionDetails() {
    return this.axiosInstance.get<ConsumptionDetails>(ENDPOINTS.accounts.consumptionDetails);
  }
}

export const createAccountService = ({ baseUrl, jwt }: { baseUrl: string; jwt?: string }) =>
  new ServiceAccounts(dynamicAxios(baseUrl, jwt));

export function useAccountsService() {
  const { baseUrl, jwt } = useServices();
  return useMemo(() => new ServiceAccounts(dynamicAxios(baseUrl, jwt)), [baseUrl, jwt]);
}

export default useAccountsService;

export * from './types';
